import dayjs from 'dayjs';

type LatLng = {
  lt: number;
  lg: number;
};

type Location = {
  name: string;
  lt: number;
  lg: number;
  lnk: string;
  city?: string;
  state?: string;
  country?: string;
};

type DataLayer = {
  search_destination_lat_long?: string;
  page_url_query_string?: string;
  res_stay_length?: string;
  search_destination_city?: string;
  search_destination_state?: string;
  search_destination_country?: string;
  /** Check-in date in 'YYYY-MM-DD' format */
  chckInDat?: string;
  /** Check-out date in 'YYYY-MM-DD' format */
  chckOutDate?: string;
};

type BrandPayload = {
  brandPayload?: Record<string, string>[];
  brandParams?: Record<string, string>;
};

type BrandData = {
  brands: [
    {
      hvmi: {
        brandUrl: string;

        country: Record<string, string>;
        loc: Location[];
        locHotel3nightList: Location[];
        oneNightStayList: Location[];
        state: Record<string, string>;
      };
    }
  ];
};

const R = 6378137; // Earth radius in meters
const toRad = (deg: number) => (deg * Math.PI) / 180;

function haversineDistance(loc1: LatLng, loc2: LatLng): number {
  const lat1 = toRad(loc1.lt);
  const lat2 = toRad(loc2.lt);
  const dLat = toRad(loc2.lt - loc1.lt);
  const dLng = toRad(loc2.lg - loc1.lg);

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return (2 * R * Math.asin(Math.sqrt(a))) / 1609.34; // miles
}

function getNearestLocation(locations: Location[], center: LatLng): Location | null {
  if (locations.length === 0) return null;
  const nearest = locations.reduce((prev, curr) =>
    haversineDistance(prev, center) < haversineDistance(curr, center) ? prev : curr
  );
  return haversineDistance(nearest, center) <= 100 ? nearest : null;
}

function getLinkUrl(locations: Location[], name: string): string | undefined {
  return locations.find(loc => loc.name.toUpperCase() === name.toUpperCase())?.lnk;
}

function matchesLocation(obj: Location, city?: string, state?: string, country?: string): boolean {
  return (
    (obj.city === city && obj.state === state && obj.country === country) ||
    (obj.city === city && obj.country === country && !obj.state) ||
    (obj.country === country && !obj.city && !obj.state)
  );
}

function isOneNightStayAllowed(oneNightStayList: Location[], city?: string, state?: string, country?: string): boolean {
  return !oneNightStayList.some((obj: Location) => matchesLocation(obj, city, state, country));
}

function isTwoNightStayAllowed(
  locHotel3nightList: Location[],
  city?: string,
  state?: string,
  country?: string
): boolean {
  return !locHotel3nightList.some((obj: Location) => matchesLocation(obj, city, state, country));
}

export function targetBrandBannerPayload(brandData: BrandData, dataLayer: DataLayer): BrandPayload {
  if (!(brandData && brandData.brands?.length > 0 && brandData.brands[0].hvmi)) {
    return {
      brandParams: {},
      brandPayload: [],
    } as BrandPayload;
  }
  const {
    country: countryMap,
    loc: locations,
    locHotel3nightList,
    oneNightStayList,
    state: stateMap,
    brandUrl,
  } = brandData.brands[0].hvmi;
  let hvmiDest = '';
  let isHvmiDest = false;
  let isTwoNightAllowed = false;
  let isOneNightAllowed = false;

  const {
    search_destination_lat_long,
    page_url_query_string,
    search_destination_city: city,
    search_destination_state: stateCode = '',
    search_destination_country: countryCode = '',
  } = dataLayer;

  const state = stateMap[stateCode];
  const country = countryMap[countryCode];

  let latLng: LatLng | undefined;

  if (search_destination_lat_long) {
    const [latRaw, lngRaw] = search_destination_lat_long.split(',').map(Number);
    if (!isNaN(latRaw) && !isNaN(lngRaw)) {
      latLng = { lt: latRaw, lg: lngRaw };
    }
  } else if (page_url_query_string?.includes('destinationAddress.longitude')) {
    const params = new URLSearchParams(page_url_query_string.replace(/&amp;/g, '&'));
    const lat = parseFloat(params.get('destinationAddress.latitude') || '');
    const lng = parseFloat(params.get('destinationAddress.longitude') || '');
    if (!isNaN(lat) && !isNaN(lng)) {
      latLng = { lt: lat, lg: lng };
    }
  }

  if (latLng) {
    const nearest = getNearestLocation(locations, latLng);
    if (nearest) {
      isHvmiDest = true;
      const link = `/search${nearest.lnk}?`;
      hvmiDest = JSON.stringify({
        name: nearest.name,
        distance: haversineDistance(nearest, latLng),
        linkURL: link,
      });
    }
  }

  if (!hvmiDest && state) {
    const link = getLinkUrl(locations, state);
    if (link) {
      hvmiDest = JSON.stringify({
        name: state,
        distance: '0.0',
        linkURL: `${link}?`,
      });
      isHvmiDest = true;
    }
  }

  if (!hvmiDest && country) {
    const link = getLinkUrl(locations, country);
    if (link) {
      hvmiDest = JSON.stringify({
        name: country,
        distance: '0.0',
        linkURL: `${link}?`,
      });
      isHvmiDest = true;
    }
  }

  if (isHvmiDest) {
    isTwoNightAllowed = isTwoNightStayAllowed(locHotel3nightList, city, stateCode, countryCode);
    isOneNightAllowed = isOneNightStayAllowed(oneNightStayList, city, stateCode, countryCode);
  }

  return {
    brandPayload: [
      {
        name: 'is_hvmi_dest',
        value: String(isHvmiDest),
      },
      {
        name: 'res_hvmi_dest',
        value: hvmiDest,
      },
      {
        name: 'is_two_night_allowed',
        value: String(isTwoNightAllowed),
      },
      {
        name: 'is_one_night_allowed',
        value: String(isOneNightAllowed),
      },
    ],
    brandParams: { destinationUrl: isHvmiDest ? getDestinationUrl(dataLayer, hvmiDest, brandUrl) : '' },
  };
}

// Extracted constants
const REFERRAL_QUERY = '&referral=mcom';

/**
 * Constructs a destination URL for the Homes & Villas by Marriott International (HVMI) site
 * using the search location and travel dates from the dataLayer and a resolved HVMI destination object.
 *
 * @param dataLayer - Object containing check-in and check-out dates.
 * @param hvmiDest - A JSON string of the nearest HVMI destination, with a `linkURL` field.
 *
 * @returns A formatted URL string pointing to the HVMI search result page with optional date and referral parameters.
 *
 * @example
 * ```ts
 * const dataLayer = {
 * chckInDat: '2025-06-20',
 * chckOutDate: '2025-06-22'
 * };
 *
 * const hvmiDest = '{"name":"Guttenberg","distance":2.92,"linkURL":"/search/search/guttenberg-new-jersey-home-villa-rental?"}';
 *
 * const url = getDestinationUrl(dataLayer, hvmiDest);
 * // "https://homes-and-villas.marriott.com/en/search/guttenberg-new-jersey-home-villa-rental?&fromDate=2025-06-20&toDate=2025-06-22&referral=mcom"
 * ```
 */
export const getDestinationUrl = (dataLayer: DataLayer, hvmiDest?: string, brandUrl?: string): string => {
  const checkInDate = dataLayer.chckInDat?.trim();
  const checkOutDate = dataLayer.chckOutDate?.trim();
  let dateQuery = '';

  // Build date query string if both dates are valid
  if (
    checkInDate &&
    checkOutDate &&
    dayjs(checkInDate, 'YYYY-MM-DD', true).isValid() &&
    dayjs(checkOutDate, 'YYYY-MM-DD', true).isValid()
  ) {
    const formattedCheckIn = dayjs(checkInDate).format('YYYY-MM-DD');
    const formattedCheckOut = dayjs(checkOutDate).format('YYYY-MM-DD');
    dateQuery = `&fromDate=${formattedCheckIn}&toDate=${formattedCheckOut}`;
  }

  // Try to extract and use the linkURL from the hvmiDest string
  if (typeof hvmiDest === 'string' && hvmiDest.trim()) {
    try {
      const parsed = JSON.parse(hvmiDest);
      if (parsed.linkURL && typeof parsed.linkURL === 'string') {
        const cleanedLink = parsed.linkURL.replace('search/search', 'search');
        const finalUrl = `${brandUrl}${cleanedLink}${dateQuery}${REFERRAL_QUERY}`;
        return finalUrl;
      } else {
        console.warn('Invalid or missing linkURL in hvmiDest:', parsed);
      }
    } catch (error) {
      console.warn('Failed to parse hvmiDest JSON:', hvmiDest, error);
    }
  }

  // Fallback to base URL with only date and referral if link is unavailable
  const fallbackUrl = `${brandUrl}?${dateQuery}${REFERRAL_QUERY}`;
  return fallbackUrl;
};
